import { useMemo } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import {
  COLOR_HISTOGRAM_BAR,
  COLOR_HISTOGRAM_LABEL,
  NEGOTIATION_OVERVIEW_CHART_HEIGHT,
  PRICE_DELTA_HISTOGRAM_INFO,
} from 'components/reports/Metrics/constants/NegotiationOverview'
import { BidNegotiationHistogramDto } from 'generated/graphql'
import { toLines } from 'helpers/bucketBarChart.helpers'

import { BucketBarChart } from '../components/BucketBarChart'

interface Props {
  data?: BidNegotiationHistogramDto | null
  loading?: boolean
}

export const PriceDeltaHistogramCard = ({ data, loading }: Props) => {
  const { labels, series, labelLines } = useMemo(() => {
    const buckets = data?.buckets ?? []

    return {
      labels: buckets.map((bucket) => toLines(bucket.label)),
      series: [
        {
          label: 'Jobs',
          values: buckets.map((bucket) => bucket.jobCount),
          color: COLOR_HISTOGRAM_BAR,
        },
      ],
      // The mock-up writes the count with its percentage share underneath each bar.
      labelLines: (value: number, index: number) => [
        value.toLocaleString(),
        `(${(buckets[index]?.percentage ?? 0).toLocaleString()}%)`,
      ],
    }
  }, [data])

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderTop: `3px solid ${COLOR_HISTOGRAM_BAR}`,
      }}>
      <Stack direction='row' spacing={1} alignItems='center' mb={0.5}>
        <Typography variant='subtitle1' fontWeight={700}>
          Distribution of Price Delta (Original Bid Price vs. Final Negotiated Price)
        </Typography>
        <Tooltip
          title={PRICE_DELTA_HISTOGRAM_INFO}
          slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        </Tooltip>
      </Stack>

      <Typography variant='body2' color='primary.main' mb={2}>
        Histogram showing the distribution of price delta for negotiated winning bids.
      </Typography>

      {loading || !data ? (
        <Skeleton variant='rectangular' height={NEGOTIATION_OVERVIEW_CHART_HEIGHT} />
      ) : (
        <>
          <BucketBarChart
            labels={labels}
            series={series}
            xAxisLabel='Price delta (Final negotiated price − Original bid price)'
            height={NEGOTIATION_OVERVIEW_CHART_HEIGHT}
            valueLabelLines={labelLines}
            valueLabelColor={COLOR_HISTOGRAM_LABEL}
          />

          <Typography variant='caption' color='text.secondary' display='block' mt={1}>
            {`${data.chartedJobs.toLocaleString()} of ${data.negotiatedJobs.toLocaleString()} ` +
              'negotiated jobs are charted. ' +
              `${data.zeroDeltaJobs.toLocaleString()} returned to the original price (0% delta) ` +
              'and are excluded from the chart.' +
              (data.excludedJobs > 0
                ? ` ${data.excludedJobs.toLocaleString()} could not be charted because no ` +
                  'percentage change can be calculated for them.'
                : '')}
          </Typography>
        </>
      )}
    </Paper>
  )
}
